import { useState, useEffect, useRef, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { motion } from 'framer-motion';


//トップページを構成する、パーツを読み込む

import './App.css';
import HamburgerMenu from './HamburgerMenu';

import Home from './pages/top/Top';
import About from './pages/about/About';
import Gallery from './pages/gallery/Gallery';
import Character from './pages/character/character';
import Software from './pages/software/software';
import Works from './pages/works/Works';
import Blog from './pages/blog/Blog';
import BlogDetail from './pages/blog/BlogDetail';
import Links from './pages/links/Links';

import CharacterDetail from './pages/character/CharacterDetail';
import ModalDetail from './pages/gallery/ModalDetail';
import SoftwareDetail from './pages/software/SoftwareDetail';

import { AnimatePresence } from 'framer-motion'; // AnimatePresenceをインポート
import LoadingScreen from './components/LoadingScreen';
import AdBanner from './components/AdBanner';

import { RiTwitterXFill } from 'react-icons/ri';
import { SiPixiv, SiBluesky } from 'react-icons/si';

import { FaAngleDoubleUp } from "react-icons/fa";

// Firebaseのインポート
import { auth, db } from './firebase';
import { onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const AppContent = () => {
  const location = useLocation();
  // LocationStateの型定義をインラインで行うか、anyで回避して確実に動かします
  const background = useMemo(() => {
    return (location.state as any)?.backgroundLocation || 
           (location.pathname.startsWith('/illust/') ? { pathname: '/gallery' } : null);
  }, [location]);
  const navigate = useNavigate();
  const navType = useNavigationType();
  const prevLocationRef = useRef(location); // 直前のlocationを記憶するためのref

  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false); // 管理者状態

  // セキュリティログを記録する関数
  const logSecurityEvent = async (type: string, email?: string) => {
    try {
      // ipify APIを使用してIPアドレスを取得
      const ipRes = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipRes.json();
      
      await addDoc(collection(db, 'security_logs'), {
        event: type,
        email: email || 'Unknown',
        ip: ipData.ip,
        userAgent: navigator.userAgent,
        timestamp: serverTimestamp()
      });
    } catch (err) {
      console.error("Failed to log security event:", err);
    }
  };

  // ログイン状態の監視
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && user.email === process.env.REACT_APP_ADMIN_EMAIL) {
        setIsAdmin(true);
      } else if (user) {
        // 管理者以外のユーザーがログインしてしまった場合はログを記録してログアウト
        logSecurityEvent("Unauthorized Login Attempt", user.email || "");
        signOut(auth);
        setIsAdmin(false);
      } else {
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // ★ 隠しコマンド（キー入力）の検知
  useEffect(() => {
    const secretCode = ['a', 'd', 'm', 'i', 'n']; // 入力が必要なキー配列
    let inputBuffer: string[] = [];

    const handleKeyDown = async (e: KeyboardEvent) => {
      // ★ 追加: 入力要素（input, textarea, contenteditable）での入力中は無視する
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.isContentEditable
      ) {
        return;
      }

      inputBuffer.push(e.key.toLowerCase());
      inputBuffer = inputBuffer.slice(-secretCode.length);

      if (inputBuffer.join('') === secretCode.join('')) {
        if (isAdmin) {
          if (window.confirm("ログアウトしますか？")) {
            await signOut(auth);
            alert("ログアウトしました。");
            window.location.reload();
          }
          return;
        }

        const provider = new GoogleAuthProvider();
        try {
          const result = await signInWithPopup(auth, provider);
      
          // ログイン成功したユーザーのメールアドレスを確認
          const user = result.user;
          if (user.email === process.env.REACT_APP_ADMIN_EMAIL) {
            alert("ログインしました。");

          } else {
            // 許可されていないユーザーの場合
            logSecurityEvent("Unauthorized Login Attempt", user.email || "");
            await signOut(auth); // 即座にログアウト
            alert("ログインに失敗しました。");
            alert("不正にログインしようとするのはダメですよ。");
          }

        } catch (error) {
          logSecurityEvent("Login Error/Cancel");
          console.error("Google Login Error:", error);
          
          alert("ログインに失敗しました。");
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAdmin]);

  // スクロール位置を監視して「ページトップ」ボタンの表示を切り替える
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 700) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.navigator.vibrate?.(10);
  };

  // モバイル判定
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };
    checkMobile();
  }, []);

  // 読み込み中のみスクロールを禁止し、スクロールバーを非表示にする
  useEffect(() => {
    if (isLoading) {
      document.documentElement.classList.add('is-loading');
    } else {
      document.documentElement.classList.remove('is-loading');
    }
  }, [isLoading]);

  // モーダル（background）が表示されている時にクラスを付与
  useEffect(() => {
    if (background) {
      document.documentElement.classList.add('modal-open');
    } else {
      document.documentElement.classList.remove('modal-open');
    }
  }, [background]);

  //フッターのリンク定義
  // アイコンのリストをデータ化しておくと、後で追加・削除が楽です
  const socialLinks = [
    { icon: <RiTwitterXFill />, href: "https://x.com/a_d_z_u_k_i_", color: "#000" },
    { icon: <SiBluesky />, href: "https://bsky.app/profile/adzuki0324.bsky.social", color: "#0085FF" },
    { icon: <SiPixiv />, href: "https://www.pixiv.net/users/13909150", color: "#0097FA" },

  ];



  useEffect(() => {
    // ページ全体の読み込みが完了したらロード画面を消す
    const handleLoad = () => {
      // 少し余韻をもたせるために500msくらい待ってから消すと綺麗です
      setTimeout(() => setIsLoading(false), 800);
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
      return () => window.removeEventListener('load', handleLoad);
    }
  }, []);

  // ページ遷移時のスクロール制御（モーダル閉鎖時のみ位置を維持する特例処理）
  useEffect(() => {
    const prevLocation = prevLocationRef.current;

    // モーダルや詳細ページを閉じる（戻る）操作かどうかを判定
    // イラスト詳細(/illust/)、ソフトウェア詳細(/software/)、キャラクター詳細(/character/)からの戻り時は位置を維持
    const wasDetailRoute = prevLocation.pathname.startsWith('/illust/') || 
                           prevLocation.pathname.startsWith('/software/') ||
                           prevLocation.pathname.startsWith('/character/');
    const isClosingDetail = navType === 'POP' && wasDetailRoute;

    // 現在のページがイラスト詳細（モーダル形式）であるかを判定
    const isOpeningModal = location.pathname.startsWith('/illust/');

    // モーダルを閉じる時「以外」かつ「新たにモーダルを開く時以外」で、トップに戻すべきか判定
    if (!isClosingDetail && !isOpeningModal) {
      // 通常の遷移(PUSH)も含め、新しいページに移動する際はスクロール位置をリセットする
      const shouldScrollToTop = navType === 'PUSH' || (navType === 'POP' && !isClosingDetail) || (location.state as any)?.fromMenu;
      
      if (shouldScrollToTop) {
        const isSoftwareDetail = location.pathname.startsWith('/software/');
        const scrollOptions: ScrollToOptions = { top: 0, behavior: isSoftwareDetail ? 'smooth' : 'auto' };

        // 標準のスクロールリセット
        requestAnimationFrame(() => {
          window.scrollTo(scrollOptions);
        });
      }
    }

    // 判定が終わった後に現在の場所を記憶（次の遷移の「前回」として使用）
    prevLocationRef.current = location;
  }, [location, navType]);

  // ロゴクリック時の処理
  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    try {
      const currentScrollY = window.scrollY;
 
      // 【1】トップページ以外、かつスクロールが最上部にある場合はトップへ遷移
      if (location.pathname !== '/' && currentScrollY === 0) {
        console.log('Not on home, navigating...');
        navigate('/', { state: { fromMenu: true } });
      }
      // 【2】スクロール位置が0より大きい場合は最上部へスクロール
      else if (currentScrollY > 0) {
        console.log('Scrolling to top from position:', currentScrollY);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } 
      // 【3】それ以外（すでにトップにいる場合）は何もしない
      else {
        console.log('Already at top');
      }
    } catch (error) {
      console.error('Error in handleLogoClick:', error);
    }
  };



  


  // アプリのメインコンテンツ
  const renderContent = () => (
    <div className={`App ${isMobile ? 'is-mobile' : ''}`}>
      {/* 画像ポップアップ（background）が表示されていない時だけヘッダーを表示 */}
      {!background && (
        <header className="main-header" style={{ zIndex: 101 }}>
          {/* 左側の戻るボタン（トップページ、または戻れる履歴がない時は非表示） */}
          {(location.key !== 'default' && location.pathname !== '/') && (
            <motion.button
              onClick={() => navigate(-1)}

              whileHover={{ opacity: 0.5 }}
              transition={{ duration: 0.2 }}

              style={{
                position: 'absolute',
                left: '30px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '1.5rem',
                cursor: 'pointer',
                zIndex: 101,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10px',
                textShadow: '0 0 10px rgba(0, 0, 0, 0.5)'
              }}
              
              aria-label="前のページに戻る"
            >
              ◁
            </motion.button>
          )}

          {/* 中央のロゴ（クリックでトップへ or ページ上部へスクロール） */}
          <button
            onClick={handleLogoClick}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              zIndex: 101,
              cursor: 'default',
              background: 'none',
              border: 'none',
              padding: 0
            }}
          >
            <img src="/src-top/rogo.png" alt="サイトロゴ" style={{ maxHeight: '60px', width: 'auto', filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 1.0))' }} />
          </button>
          {/* ハンバーガーメニューボタンをヘッダー内に配置 */}
          <HamburgerMenu />
        </header>
      )}

      {/* 管理者モード通知バナー */}
      {isAdmin && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', background: 'rgba(255, 153, 0, 0.7)',
          color: 'white', textAlign: 'center', zIndex: 9999, fontSize: '0.8rem', pointerEvents: 'none'
        }}>
          管理者モードでログイン中　※編集終了後はログアウト推奨
        </div>
      )}

      {/* メインのルーティング */}
      <Routes location={background || location}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/character" element={<Character />} />
        <Route path="/software" element={<Software />} />
        <Route path="/blog" element={<Blog isAdmin={isAdmin} />} />
        <Route path="/blog/:id" element={<BlogDetail isAdmin={isAdmin} />} />
        
        <Route path="/works" element={<Works />} />
        <Route path="/links" element={<Links />} />


        <Route path="/character/:id" element={<CharacterDetail />} />
        <Route path="/illust/:id" element={<ModalDetail />} />

        <Route path="/software/:id" element={<SoftwareDetail />} />

        {/* 定義されていないURLにアクセスした場合はトップにリダイレクト 
        <Route path="*" element={<Navigate to="/" replace />} />
        */}
      </Routes>

      {/* モーダルのルーティング（背景がある時だけ重なる） */}
      {background && (
        <Routes>
          {/* path を /* にすることで、どんなIDでも一旦 ModalDetail を呼び出します */}
          <Route path="/illust/:id" element={<ModalDetail />} />
        </Routes>
      )}


      <footer style={{ padding: '2rem', textAlign: 'center', color: 'white' }}>

        <p style={{ marginTop: '1rem', fontSize: '0.8rem', opacity: 1 }}>
          © 2015-{new Date().getFullYear()} Adzuki.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
          {socialLinks.map((link, index) => (
            <motion.a
              key={index}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 0.90 }}
              style={{ fontSize: '2.0rem', color: 'inherit' }}

              onClick={() => window.navigator.vibrate?.(10)}
            >
              {link.icon}
            </motion.a>
          ))}
        </div>

        {/* フッター下部の広告エリア */}
        <div style={{ marginTop: '3rem', minHeight: '100px', width: '100%' }}>
          <AdBanner slot="2523042972" format="auto" />
        </div>

      </footer>

      {/* ページトップへ戻るボタン */}
      <AnimatePresence>
        {(showScrollTop && !background) && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: 20 }}
            onClick={scrollToTop}

            style={{
              position: 'fixed',

              bottom: '30px',
              right: '30px',
              width: '50px',
              height: '50px',

              borderRadius: '50%',
              background: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(5px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',

              color: 'white',
              fontSize: '1.2rem',
              cursor: 'pointer',
              display: 'flex',

              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              boxShadow: '0 4px 15px rgba(0,0,0,0.3)'
            }}

            whileHover={{ scale: 0.95, background: 'rgba(0, 0, 0, 0.1)' }}
            whileTap={{ scale: 0.9 }}
            aria-label="ページトップへ戻る"
          >
            <FaAngleDoubleUp />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );


  return (
    <div style={{ minHeight: '100%', width: '100%', position: 'relative' }}>
      <title>毒と花と6畳間</title>
      
      <AnimatePresence>
        {isLoading && <LoadingScreen key="loading" />}
      </AnimatePresence>

      {renderContent()}
    </div>
  );
};



const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
